import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Mail, Settings, Send, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

const EmailSettingsPage = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    smtp_server: "",
    smtp_port: 587,
    smtp_email: "",
    smtp_password: "",
    smtp_configured: false,
    notify_obvestila: true,
    notify_dogodki: true,
    notify_sporocila: true,
    notify_odstrel: false,
    notify_koce: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/email/settings`, { headers });
      setSettings(prev => ({ ...prev, ...res.data, smtp_password: "" }));
    } catch (error) {
      console.error("Error fetching email settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSMTP = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/email/settings`, {
        smtp_server: settings.smtp_server,
        smtp_port: settings.smtp_port,
        smtp_email: settings.smtp_email,
        smtp_password: settings.smtp_password || undefined
      }, { headers });
      toast.success("SMTP nastavitve shranjene");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/email/toggles`, { [key]: value }, { headers });
      toast.success("Nastavitev posodobljena");
    } catch (error) {
      toast.error("Napaka pri posodabljanju");
      setSettings(prev => ({ ...prev, [key]: !value }));
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/email/test`, {}, { headers });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri pošiljanju testnega emaila");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const toggleItems = [
    { key: "notify_obvestila", label: "Nova obvestila", desc: "Pošlji email ob objavi novega obvestila" },
    { key: "notify_dogodki", label: "Novi dogodki", desc: "Pošlji email ob ustvarjanju novega dogodka" },
    { key: "notify_sporocila", label: "Nova sporočila", desc: "Pošlji email prejemniku ob novem sporočilu" },
    { key: "notify_odstrel", label: "Spremembe odstrela", desc: "Pošlji email ob spremembah plana odstrela" },
    { key: "notify_koce", label: "Rezervacije koč", desc: "Pošlji email ob novih rezervacijah" }
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <Mail className="h-7 w-7" />
          E-mail nastavitve
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Konfiguracija SMTP in upravljanje e-mail obvestil</p>
      </div>

      {/* SMTP Configuration */}
      <Card data-testid="smtp-config-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            SMTP konfiguracija
            {settings.smtp_configured ? (
              <Badge variant="default" className="ml-2 bg-green-600">
                <CheckCircle className="h-3 w-3 mr-1" /> Konfigurirano
              </Badge>
            ) : (
              <Badge variant="secondary" className="ml-2">
                <XCircle className="h-3 w-3 mr-1" /> Ni konfigurirano
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="smtp_server">SMTP strežnik</Label>
              <Input
                id="smtp_server"
                data-testid="smtp-server-input"
                value={settings.smtp_server}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_server: e.target.value }))}
                placeholder="smtp.gmail.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="smtp_port">SMTP port</Label>
              <Input
                id="smtp_port"
                data-testid="smtp-port-input"
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                placeholder="587"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="smtp_email">E-mail naslov</Label>
            <Input
              id="smtp_email"
              data-testid="smtp-email-input"
              type="email"
              value={settings.smtp_email}
              onChange={(e) => setSettings(prev => ({ ...prev, smtp_email: e.target.value }))}
              placeholder="ld.rakek@lovskazveza.si"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="smtp_password">SMTP geslo (App Password)</Label>
            <div className="relative mt-1">
              <Input
                id="smtp_password"
                data-testid="smtp-password-input"
                type={showPassword ? "text" : "password"}
                value={settings.smtp_password}
                onChange={(e) => setSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                placeholder={settings.smtp_configured ? "••••••••  (pustite prazno za ohranitev)" : "Vnesite App Password"}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Za Google Workspace: Nastavitve &gt; Varnost &gt; 2-koračno preverjanje &gt; Gesla za aplikacije
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={handleSaveSMTP} disabled={saving} data-testid="save-smtp-btn">
              {saving ? "Shranjujem..." : "Shrani nastavitve"}
            </Button>
            <Button
              variant="outline"
              onClick={handleTestEmail}
              disabled={testing || !settings.smtp_configured}
              data-testid="test-email-btn"
            >
              <Send className="h-4 w-4 mr-1" />
              {testing ? "Pošiljam..." : "Pošlji testni email"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Toggles */}
      <Card data-testid="email-toggles-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            E-mail obvestila
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Izberite, za katere dogodke se pošiljajo e-mail obvestila
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {toggleItems.map((item) => (
            <div key={item.key} className="flex items-center justify-between py-2 border-b last:border-0">
              <div className="flex-1">
                <Label className="font-medium">{item.label}</Label>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <Switch
                data-testid={`toggle-${item.key}`}
                checked={settings[item.key]}
                onCheckedChange={(val) => handleToggle(item.key, val)}
              />
            </div>
          ))}
          {!settings.smtp_configured && (
            <p className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
              Za pošiljanje e-mail obvestil najprej konfigurirajte SMTP nastavitve zgoraj.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettingsPage;
